import { BellTwoTone } from '@ant-design/icons';
import { Card, Modal, Space, Steps } from 'antd';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import PPPageHeader from '../../../components/pageHeader/PPPageHeader';
import useStore from '../../../store/useStore';
import { AddOrderDeliveryProvider } from './AddOrderDeliveryContext';
import { AddOrderReturnProvider } from './addOrderReturnContext';
import { exchangeDraftKey, readDraft, returnDraftKey, useClearDraftOnLeave } from './draft';
import { Step1 } from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
/**
 * ⚠️ Энэ урсгал өөрөө доорх файлын классуудыг бараг хэрэглэхээ больсон. Гэвч
 * LESS глобал тул `.rd__btn_locker` (Step1/helper.js), `.modal_container`,
 * `.sel__prod__*` зэрэг нь бусад дэлгэцийн харагдацыг барьсаар байгаа —
 * тэдгээр нь өөрсдийн style-тай болох хүртэл импортыг устгаж болохгүй.
 */
import '../style.less';

const routes = [
  { path: '/merchant/order/return', breadcrumbName: 'Захиалгууд' },
  { path: 'first', breadcrumbName: 'Буцаалт' },
];

const STEPS = [
  { title: 'Бараа', description: 'Захиалгын мэдээлэл' },
  { title: 'Буцаах хаяг', description: 'Буцаалтын мэдээлэл' },
  { title: 'Хянах', description: 'Мэдээлэл баталгаажуулах' },
];

const AddOrderReturn = () => {
  const history = useHistory();
  const location = useLocation();

  const [orderBlock, setOrderBlock] = useState(false);
  const [orderBlockData, setOrderBlockData] = useState({});

  /**
   * Алхмыг URL-ийн `?step=1..3` агуулна — refresh болон browser-ийн
   * back/forward ажиллана.
   */
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  const urlStep =
    Number.isInteger(stepParam) && stepParam >= 1 ? Math.min(stepParam - 1, STEPS.length - 1) : 0;

  // Эхний рендер дээр л: draft-д дата байхгүй алхам руу шууд орохыг хориглоно
  // (жиш нь шинэ session дээр `?step=3` гэж гараар оровол 1-р алхмаас эхэлнэ)
  const isMounted = useRef(false);
  /**
   * Draft-ыг merchant-аар салгана — merchant солиход (нэг таб дотор reload
   * болдог тул) өмнөх merchant-ийн сонголт шинэ merchant дээр сэргэхгүй.
   */
  const merchantCode = useStore((state) => state.user.merchantCode);
  const draftKey = returnDraftKey(merchantCode);
  const draft = useRef(readDraft(draftKey)).current;
  // Хуудсаас гармагц бөглөсөн мэдээлэл цэвэрлэгдэнэ (refresh дээр сэргэсэн хэвээр)
  useClearDraftOnLeave(draftKey);
  /**
   * `products` нь тухайн алхмыг БАТАЛСАН үед л бичигдэнэ. `formValues` нь
   * бөглөж байх зуур ч хадгалагддаг тул түүгээр шалгаж болохгүй (reducer-ийн
   * анхны утга нь хоосон массив тул `length`-ээр биш `products`-оор шалгана).
   */
  const maxAllowedStep = draft.formDataStep2?.products
    ? 2
    : draft.formDataStep1?.products
      ? 1
      : 0;
  const current = isMounted.current ? urlStep : Math.min(urlStep, maxAllowedStep);

  /**
   * Алхам солихдоо шинэ history бичлэг үүсгэнэ — browser-ийн back дарахад
   * өмнөх алхам руу буцна. Зөвхөн зөвшөөрөгдөөгүй алхмыг залруулахдаа
   * `replace` хийнэ (буцах товч дахин тэр алхам руу оруулахгүй).
   */
  const goToStep = (step, { replace = false } = {}) => {
    const target = { pathname: location.pathname, search: `?step=${step + 1}` };
    return replace ? history.replace(target) : history.push(target);
  };

  const nextStep = () => goToStep(current + 1);
  const prevStep = () => goToStep(current - 1);

  useEffect(() => {
    isMounted.current = true;
    if (urlStep > maxAllowedStep) goToStep(maxAllowedStep, { replace: true });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    axios
      .get('/api/v1/request/check-order-block')
      .then(({ data }) => {
        if (data.statusCode !== 200) {
          setOrderBlock(true);
          setOrderBlockData(data);
        }
      })
      .catch((e) => {
        console.error('check-order-block: ', e);
      });
  }, []);

  const stepContent = [
    <Step1 nextStep={nextStep} />,
    <Step2 nextStep={nextStep} prevStep={prevStep} />,
    <Step3 prevStep={prevStep} />,
  ];

  return (
    <div>
      <PPPageHeader
        title="Буцаалтын захиалга"
        breadcrumb={{ routes }}
        onBack={() => history.push('/merchant/order/return')}
      />

      {orderBlock ? (
        <Modal
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <BellTwoTone style={{ fontSize: 14, color: '#08c' }} />
              Мэдэгдэл
            </span>
          }
          open={orderBlock}
          onCancel={() => setOrderBlock(false)}
          footer={null}
        >
          {orderBlockData?.errorMessageMn || orderBlockData?.message_mn}
        </Modal>
      ) : (
        /**
         * `key` нь draftKey — merchant солиход provider-ууд бүхэлдээ remount
         * болж төлөв нь тэглэгдэнэ. Эс бөгөөс дахин ачаалахаас өмнөх сүүлийн
         * рендерт ӨМНӨХ merchant-ийн бараа ШИНЭ merchant-ийн draft key дор
         * бичигдээд үлдэнэ.
         */
        <AddOrderReturnProvider key={draftKey} draftKey={draftKey}>
          <AddOrderDeliveryProvider draftKey={exchangeDraftKey(draftKey)}>
            <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
              <Card size="middle">
                {/**
                 * Гарчиг дээр дарж ӨМНӨХ алхам руу буцаж болно. Урагшаа алхам нь
                 * disabled — мэдээллийг батлахгүйгээр цааш харайхыг хориглоно.
                 */}
                <Steps
                  current={current}
                  responsive
                  size="small"
                  onChange={(step) => step < current && goToStep(step)}
                  items={STEPS.map((step, index) => ({ ...step, disabled: index > current }))}
                />
              </Card>

              {stepContent[current]}
            </Space>
          </AddOrderDeliveryProvider>
        </AddOrderReturnProvider>
      )}
    </div>
  );
};

export default AddOrderReturn;
